import React, { useState, useEffect, useRef } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { Button, Row, Col, Card, Spin, message, Radio, Input, Skeleton, Form, Select } from "antd";
import PaymentMethodSelector from './paymentMethodSelector.tsx';
import OrderSummary from './orderSummary.tsx';
import { CheckCircleOutlined, CheckOutlined } from "@ant-design/icons";
import { loadStripe } from "@stripe/stripe-js";
import * as com from "../Common";
import {
  Elements,
  PaymentElement,
  CardNumberElement,
  CardExpiryElement,
  CardCvcElement,
  useStripe,
  useElements,
} from "@stripe/react-stripe-js";
import "./PaymentPage.css";
import { track, flushThenLeave } from 'statsig'
import { STATSIG, PAGE, paymentPlanValue, paymentPayProps } from 'statsig/events'

export async function getLimitCount() {
  try {
    const id = com.getCustomerId();
    if (!id) return 10;
    const res = await fetch(
      `${com.getStrataApiBaseUrl()}/mai/api/v1/customer/branding/${id}`,
    );
    const data = await res.json();
    const count = data?.data?.loggedInUserUseCount;
    return typeof count === "number" ? count : 10;
  } catch (e) {
    console.log("@-getLimitCount", e);
    return 10;
  }
}

// Stripe 配置
const stripePromise = loadStripe(
  window.location.hostname.includes("localhost") ||
    window.location.hostname.includes("zeitro.us")
    ? "pk_test_51OEzY9I66V2JvOwpc9hJAIZo8RxLTiPO33iv1Takv419F9AhTAxX35VfJEvyJZX02d68Uqm6WyvErE84MyNVrQzR000acdv6i1"
    : "pk_live_51OEzY9I66V2JvOwp5hPwHHCHd2bzwgK9KKIBwHsVqgyzKjCbJCnMJdfJxhdcoH2kurI1M9qm6TYYlBHQtq1NRKhM00HstjtkWY"
);

// 套餐数据 - 参考 planPricing 的实现
const pricingPlans = [
  {
    id: "starter",
    name: "Explorer",
    price: "FREE",
    period: "Individuals testing the platform.",
    buttonText: "Get started free",
    popular: false,
    priceId: null, // 免费套餐不需要价格ID
    features: [
      "FNM / 1003 exports (10 lifetime)",
      "Strata AI (10 per day)",
      "Personal website",
      "Pricing engine",
      "Mortgage call reports",
      "AI tools (Income, Document Review, Conditions)",
      //   "Agent personal webpage",
      //   "Rate & Affordability Calculator widget",
      //   "Document AI Processing",
    ],
  },
  {
    id: "originator",
    name: "Individual",
    price: "$8",
    period: "Individual loan professionals seeking efficiency.",
    buttonText: "Activate now",
    popular: true,
    priceId: null, // 将从API获取
    features: [
      "Everything in Explorer",
      "Unlimited FNM / 1003 exports",
      "Unlimited Strata AI",
      "Whitelabeled personal website",
      "OpenHouse Finder",
      "Rate Marketplace",
      //   "Rate & Affordability Calculator widget",
      //   "Document AI Processing",
    ],
  },
  {
    id: "business",
    name: "Business",
    price: "$35",
    period:
      "Growing teams(up to 30 users) that need collaboration and smarter workflows",
    buttonText: "Activate now",
    popular: false,
    priceId: null, // 将从API获取
    features: [
      "Everything in Originator",
      "APIs & LOS/CRM integrations",
      "Dedicated AE",
      // "Homeowner & OpenHouse Marketplaces",
      //   "Credit pull integration",
      //   "Agent personal webpage & customized url",
      //   "Rate & Affordability Calculator widget",
      //   "Document AI Processing",
      //   "Team & Branch Setup",
    ],
  },
];

// 支付表单组件
function PaymentForm({ selectedPlan, onPaymentSuccess, onBack, clientSecret, setClientSecret, createSetupIntent, stripeCustomer, isMobile, fetchUserStripeCustomer }) {
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);
  const [isPay, setIsPay] = useState(false);
  const [isChecked, setIsChecked] = useState(true); // 年付/月付选择
  const [promoCode, setPromoCode] = useState("");
  const [promoCodeApplied, setPromoCodeApplied] = useState(false);
  const [promoCodeError, setPromoCodeError] = useState(false);
  const [appliedPromoCode, setAppliedPromoCode] = useState("");
  const [promoCodeExpanded, setPromoCodeExpanded] = useState(false);
  const [paymentFormData, setPaymentFormData] = useState(null);
  const paymentMethodRef = useRef();

  const svg_logo = () => {
    return (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="122"
        height="28"
        viewBox="0 0 122 28"
        fill="none"
      >
        <path
          d="M72.6758 6.09116C73.4678 6.09116 74.1349 5.8184 74.6837 5.27289C75.2292 4.72737 75.5019 4.05698 75.5019 3.265C75.5019 2.47301 75.2292 1.80262 74.6837 1.25711C74.1382 0.711591 73.4678 0.435547 72.6758 0.435547C71.8838 0.435547 71.2167 0.708304 70.6679 1.25711C70.1224 1.79934 69.8496 2.47301 69.8496 3.265C69.8496 4.05698 70.1224 4.72409 70.6679 5.27289C71.2134 5.81512 71.8838 6.09116 72.6758 6.09116Z"
          fill="#081265"
        />
        <path
          d="M75.0842 7.47176H70.2666V24.7442H75.0842V7.47176Z"
          fill="#081265"
        />
        <path
          d="M84.8735 10.9403V18.6794C84.8735 19.3432 85.0444 19.8624 85.3894 20.2371C85.7312 20.6117 86.1354 20.799 86.6086 20.799C87.5058 20.799 88.2649 20.3915 88.886 19.5798L90.6835 22.9515C89.185 24.3219 87.6043 25.0054 85.9481 25.0054C84.2918 25.0054 82.8853 24.4632 81.7417 23.3689C80.5981 22.2778 80.0263 20.7892 80.0263 18.9062V10.9436H78.002V7.47662H80.0263V2.30408H84.8735V7.47333H89.047V10.9403H84.8735Z"
          fill="#081265"
        />
        <path
          d="M101.826 11.7069C100.393 11.7069 99.3216 12.2163 98.615 13.2317C97.9085 14.2472 97.5569 15.5912 97.5569 17.2606V24.7401H92.7393V7.47094H97.5569V9.75158C98.1747 9.04504 98.9535 8.44366 99.8835 7.95401C100.814 7.46108 101.76 7.20475 102.723 7.18175L102.756 11.7069H101.826Z"
          fill="#081265"
        />
        <path
          d="M43.5803 7.50485H34.8293L32.1367 11.5438H40.8877L32.1367 24.6702H46.6095L49.3021 20.6313H40.5511L49.3021 7.50485H43.5803Z"
          fill="#081265"
        />
        <path
          fill-rule="evenodd"
          clip-rule="evenodd"
          d="M113.08 25.0047C118.006 25.0047 122 21.0114 122 16.0854C122 11.1594 118.006 7.16618 113.08 7.16618C108.155 7.16618 104.161 11.1594 104.161 16.0854C104.161 21.0114 108.155 25.0047 113.08 25.0047ZM113.08 20.4609C115.497 20.4609 117.456 18.5019 117.456 16.0854C117.456 13.6689 115.497 11.7099 113.08 11.7099C110.664 11.7099 108.705 13.6689 108.705 16.0854C108.705 18.5019 110.664 20.4609 113.08 20.4609Z"
          fill="#081265"
        />
        <path
          fill-rule="evenodd"
          clip-rule="evenodd"
          d="M61.3364 24.5937C63.8699 23.9148 65.8376 22.4349 67.2434 20.153L63.879 18.0335C63.1184 19.4109 61.92 20.3207 60.2882 20.758C59.2792 21.0283 58.2906 20.9836 57.3294 20.622C56.3682 20.2604 55.6927 19.6417 55.3026 18.77L67.4598 15.5125L66.7947 13.0302C66.1465 10.6114 64.8361 8.9212 62.8626 7.96656C60.8885 7.01224 58.7575 6.84158 56.4621 7.45664C54.1673 8.07153 52.3848 9.37926 51.114 11.38C49.8479 13.3829 49.5613 15.6916 50.2656 18.3199C50.9698 20.9481 52.3558 22.8188 54.4309 23.9366C56.5026 25.0519 58.8034 25.2724 61.3364 24.5937ZM54.5469 15.4419C54.1028 13.7844 55.3714 11.8236 57.4363 11.2703C59.5012 10.717 61.5803 11.7809 62.0244 13.4383L54.5469 15.4419Z"
          fill="#081265"
        />
        <path
          fill-rule="evenodd"
          clip-rule="evenodd"
          d="M6.14337 22.9597L5.45206 25.0336C5.12058 26.0281 5.86076 27.055 6.90899 27.055H21.5004C23.1967 27.055 24.5718 25.6799 24.5718 23.9836L24.5722 7.60232C24.5723 5.90596 23.1971 4.53076 21.5007 4.53076H18.4288L14.3335 16.8167H20.5788L18.5312 22.9597H6.14337Z"
          fill="#1630AC"
        />
        <path
          fill-rule="evenodd"
          clip-rule="evenodd"
          d="M6.14298 22.9598H3.07149C1.37515 22.9598 0 21.5847 0 19.8883V3.50704C0 1.8107 1.37515 0.435547 3.07149 0.435547H17.6633C18.7115 0.435547 19.4517 1.46249 19.1203 2.45694L18.4289 4.53087H6.14298L4.09532 10.6738H10.2383L6.14298 22.9598Z"
          fill="#081265"
        />
      </svg>
    );
  };

  const handleApplyPromoCode = () => {
    if (!promoCode.trim()) return;

    // Check for valid promo codes
    const validPromoCodes = [
      "MAXCLASS90",
      "MAXCLASS60",
      "ZEITRO30",
      "WELCOME30",
      "SPECIAL30",
      "ORIGINATOR60",
      "WELCOME60",
      "SPECIAL60",
      "PREMIUM90",
      "WELCOME90",
      "SPECIAL90",
      "FRIENDS90",
      "FRIENDS180",
    ];
    const code = promoCode.toUpperCase();
    if (validPromoCodes.includes(code)) {
      setPromoCodeApplied(true);
      setPromoCodeError(false);
      // If yearly plan, append YEAR to the promo code
      setAppliedPromoCode(isChecked ? `${code}YEAR` : code);
    } else {
      setPromoCodeError(true);
      setPromoCodeApplied(false);
    }
  };

  const handleRemovePromoCode = () => {
    setPromoCodeApplied(false);
    setPromoCode("");
    setAppliedPromoCode("");
    setPromoCodeError(false);
  };

  const handlePaymentWithExistingData = async (clientSecretToUse) => {
    console.log("stripeCustomer in handlePaymentWithExistingData:", stripeCustomer);
    console.log("selectedLoanOfficerIDs being sent:", [com.getLoId()]);
    
    if (!stripe || !elements) {
      message.error("Payment system not ready. Please try again.");
      return;
    }

    // Use the provided clientSecret or fall back to state
    const secretToUse = clientSecretToUse || clientSecret;
    if (!secretToUse) {
      message.error("Payment session expired. Please try again.");
      return;
    }

    try {
      // 从 PaymentMethodSelector 中获取卡片信息
      const cardElement = elements.getElement(CardNumberElement);
      if (!cardElement) {
        message.error('Please enter your card information');
        return;
      }

      // 创建支付方式
      const result = await stripe.createPaymentMethod({
        type: "card",
        card: cardElement,
      });

      if (result.error) {
        message.error(result.error.message);
        const pay = paymentPayProps(selectedPlan.id, isChecked, 'fail', 'signup')
        if (pay) track(STATSIG.PAYMENT.PAY, pay.value, pay.metadata)
        return;
      }

      // 确认卡片设置
      const { setupIntent, error: setupError } = await stripe.confirmCardSetup(secretToUse, {
        payment_method: result.paymentMethod.id,
      });

      if (setupError) {
        message.error(setupError.message || "Payment setup failed. Please try again.");
        const pay = paymentPayProps(selectedPlan.id, isChecked, 'fail', 'signup')
        if (pay) track(STATSIG.PAYMENT.PAY, pay.value, pay.metadata)
        return;
      }
      console.log('setupIntent---------', {
        paymentMethodId: setupIntent.payment_method,
        billingFrequency: isChecked ? "annual" : "monthly",
        planType: selectedPlan.id,
        selectedLoanOfficerIDs: [com.getLoId()], // 传递当前用户的ID
        stripeCustomerID: stripeCustomer?.customer?.id || setupIntent.customer || stripeCustomer?.id,
        promoCode: appliedPromoCode || null, // 传递促销码
      })

      // setTimeout(() => {
      //   onPaymentSuccess()
      // }, 3000)
      // return;

      // 创建订阅
      const token = sessionStorage.getItem("ZeitroA");
      const subscriptionResponse = await fetch("/los/subscribetopaidplan", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: "Bearer " + token,
          Cache: "no-cache",
        },
        body: JSON.stringify({
          paymentMethodId: setupIntent.payment_method,
          billingFrequency: isChecked ? "annual" : "monthly",
          planType: selectedPlan.id,
          selectedLoanOfficerIDs: [com.getLoId()], // 传递当前用户的ID
          stripeCustomerID: stripeCustomer?.customer?.id || setupIntent.customer || stripeCustomer?.id,
          promoCode: appliedPromoCode || null, // 传递促销码
        }),
      });

      if (subscriptionResponse.status !== 200) {
        const errorData = await subscriptionResponse.json().catch(() => ({}));
        message.error(errorData.error || 'Something went wrong, please try later');
        const pay = paymentPayProps(selectedPlan.id, isChecked, 'fail', 'signup')
        if (pay) track(STATSIG.PAYMENT.PAY, pay.value, pay.metadata)
        return;
      }

      message.success("Subscription created successfully!");
      const pay = paymentPayProps(selectedPlan.id, isChecked, 'success', 'signup')
      if (pay) track(STATSIG.PAYMENT.PAY, pay.value, pay.metadata)
      onPaymentSuccess();
    } catch (error) {
      console.error("Payment error:", error);
      message.error("Payment failed. Please try again.");
      const pay = paymentPayProps(selectedPlan.id, isChecked, 'fail', 'signup')
      if (pay) track(STATSIG.PAYMENT.PAY, pay.value, pay.metadata)
    }
  };

  const getUSD = () => {
    const basePrice = selectedPlan.id === "business" ? 35 : 8;
    const annualPrice = selectedPlan.id === "business" ? 30 : 6.67;
    return isChecked ? annualPrice : basePrice;
  };

  const getTotal = () => {
    const price = getUSD();
    let basePrice;
    
    if (isChecked) {
      // 年付时，需要乘以12个月
      basePrice = price * 12;
    } else {
      // 月付时
      basePrice = price;
    }

    if (!promoCodeApplied) {
      return basePrice.toFixed(2);
    }

    const code = appliedPromoCode.toUpperCase();
    
    if (isChecked) {
      // Yearly plan discounts
      if (code === 'MAXCLASS90YEAR') {
        if(selectedPlan.id === "business") {
          return (basePrice * (9/12)).toFixed(2); // 3/12 discount
        } else {
          return (80 * (9/12)).toFixed(2); // 3/12 discount
        }
      } else if (code === 'MAXCLASS60YEAR') {
        if(selectedPlan.id === "business") {
          return (basePrice * (10/12)).toFixed(2); // 2/12 discount
        } else {
          return (80 * (10/12)).toFixed(2); // 2/12 discount
        }
      } else if (code === 'ZEITRO30YEAR' || code === 'WELCOME30YEAR' || code === 'SPECIAL30YEAR') {
        if(selectedPlan.id === "business") {
          return (basePrice * (11/12)).toFixed(2); // 1/12 discount
        } else {
          return (80 * (11/12)).toFixed(2); // 1/12 discount
        }
      } else if (code === 'ORIGINATOR60YEAR' || code === 'WELCOME60YEAR' || code === 'SPECIAL60YEAR') {
        if(selectedPlan.id === "business") {
          return (basePrice * (10/12)).toFixed(2); // 2/12 discount
        } else {
          return (80 * (10/12)).toFixed(2); // 2/12 discount
        }
      } else if (code === 'PREMIUM90YEAR' || code === 'WELCOME90YEAR' || code === 'SPECIAL90YEAR' || code === 'FRIENDS90YEAR') {
        if(selectedPlan.id === "business") {
          return (basePrice * (9/12)).toFixed(2); // 3/12 discount
        } else {
          return (80 * (9/12)).toFixed(2); // 3/12 discount
        }
      } else if (code === 'FRIENDS180YEAR') {
        if(selectedPlan.id === "business") {
          return (basePrice * (6/12)).toFixed(2); // 6/12 discount
        } else {
          return (80 * (6/12)).toFixed(2); // 6/12 discount
        }
      }
    } else {
      // Monthly plan discounts
      if (code === 'MAXCLASS90') {
        return "0.00"; // Full discount for monthly
      } else if (code === 'MAXCLASS60') {
        return "0.00"; // Full discount for monthly
      } else if (code === 'ZEITRO30' || code === 'WELCOME30' || code === 'SPECIAL30') {
        return "0.00"; // Full discount for monthly
      } else if (code === 'ORIGINATOR60' || code === 'WELCOME60' || code === 'SPECIAL60') {
        return "0.00"; // Full discount for monthly
      } else if (code === 'PREMIUM90' || code === 'WELCOME90' || code === 'SPECIAL90' || code === 'FRIENDS90') {
        return "0.00"; // Full discount for monthly
      } else if (code === 'FRIENDS180') {
        return "0.00"; // Full discount for monthly
      }
    }
    
    return basePrice.toFixed(2);
  };

  const getPromoCodeDiscount = () => {
    if (!promoCodeApplied) {
      return 0;
    }

    const price = getUSD();
    let basePrice;
    
    if (isChecked) {
      // 年付时，需要乘以12个月
      basePrice = price * 12;
    } else {
      // 月付时
      basePrice = price;
    }

    const code = appliedPromoCode.toUpperCase();
    
    if (isChecked) {
      // Yearly plan discounts - 基于年度折扣后的价格计算
      // basePrice 已经是年度折扣后的价格（$80.00 或 $360.00）
      if (code === 'MAXCLASS90YEAR') {
        if(selectedPlan.id === "business") {
          return basePrice * (3/12); // 3/12 discount
        } else {
          return 80 * (3/12); // 3/12 discount
        }
      } else if (code === 'MAXCLASS60YEAR') {
        if(selectedPlan.id === "business") {
          return basePrice * (2/12); // 2/12 discount
        } else {
          return 80 * (2/12); // 2/12 discount
        }
      } else if (code === 'ZEITRO30YEAR' || code === 'WELCOME30YEAR' || code === 'SPECIAL30YEAR') {
        if(selectedPlan.id === "business") {
          return basePrice * (1/12); // 11/12 discount
        } else {
          return 80 * (1/12); // 11/12 discount
        }
      } else if (code === 'ORIGINATOR60YEAR' || code === 'WELCOME60YEAR' || code === 'SPECIAL60YEAR') {
        if(selectedPlan.id === "business") {
          return basePrice * (2/12); // 2/12 discount
        } else {
          return 80 * (2/12); // 2/12 discount
        }
      } else if (code === 'PREMIUM90YEAR' || code === 'WELCOME90YEAR' || code === 'SPECIAL90YEAR' || code === 'FRIENDS90YEAR') {
        if(selectedPlan.id === "business") {
          return basePrice * (3/12); // 3/12 discount
        } else {
          return 80 * (3/12); // 3/12 discount
        }
      } else if (code === 'FRIENDS180YEAR') {
        if(selectedPlan.id === "business") {
          return basePrice * (6/12); // 6/12 discount
        } else {
          return 80 * (6/12); // 6/12 discount
        }
      }
    } else {
      // Monthly plan discounts - 所有促销码都是100%折扣
      // 月付时，促销码折扣等于月价格（100%折扣）
      return basePrice;
    }
    
    return 0;
  };




  if (selectedPlan.id === "starter") {
    return (
      <div className="payment-form">
        <div className="payment-summary">
          <h3>Free Plan Selected</h3>
          <p>You've selected the free Starter plan. No payment required.</p>
        </div>
        <div className="payment-actions">
          <Button onClick={onBack} style={{ marginRight: 16 }}>
            Back
          </Button>
          <Button type="primary" onClick={onPaymentSuccess} loading={loading}>
            Continue to Welcome
          </Button>
        </div>
      </div>
    );
  }

  // 参考 planPricing 的流程：先显示升级详情，然后点击 Continue 才显示支付表单
  if (!isPay) {
    return (
      <div className="payment-form">
        <div className="back-header">
          <div className="payment-page-svg">{svg_logo()}</div>
          <div
            className="back-title"
            onClick={() => {
              onBack();
            }}
          >
            <span style={{ marginRight: 8, padding: "7px 4px" }}>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="8"
                height="13"
                viewBox="0 0 8 13"
                fill="none"
              >
                <path
                  d="M6.29871 12.1342L7.35938 11.0736L1.70252 5.4167L0.64186 6.47736L6.29871 12.1342Z"
                  fill="#325CEB"
                />
                <path
                  d="M7.35926 1.88031L6.2986 0.81965L0.64186 6.47736L1.70241 7.53716L7.35926 1.88031Z"
                  fill="#325CEB"
                />
              </svg>
            </span>
            Select your plan
          </div>
          {/* <div className="back-header-title">Payment</div> */}
        </div>
        <div className={`billing-content-box ${isMobile ? 'mobile-layout' : ''}`}>
          <div className="payment-form-content">
            {/* 左侧：Billing frequency */}
            <div style={{ flex: isMobile ? 'none' : 1 }} className="payment-form-content-type-box">
              <div>
                <div className="payment-page-title-confirm-title">
                  Confirm your billing cycle
                </div>
                <div className="payment-page-title-confirm">
                  <span className="payment-page-title-confirm-save">
                    Save ${selectedPlan.id === "business" ? 60 : 16}/year &nbsp;
                  </span>
                  <span className="payment-page-title-confirm-when">
                    when you select annual billing cycle
                  </span>
                </div>
              </div>

              {/* Annual option */}
              <div
                style={{
                  border: isChecked ? "2px solid #325CEB" : "1px solid #D9D9D9",
                  borderRadius: "10px",
                  padding: "12px",
                  marginBottom: "12px",
                  cursor: "pointer",
                  background: "#fff",
                }}
                onClick={() => {
                  setIsChecked(true);
                  // 切换计费周期时清除促销码状态
                  if (promoCodeApplied) {
                    setPromoCodeApplied(false);
                    setPromoCode("");
                    setAppliedPromoCode("");
                    setPromoCodeError(false);
                  }
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "baseline",
                    marginBottom: "8px",
                  }}
                >
                  <Radio checked={isChecked} />
                  <div
                    style={{
                      display: "flex",
                      alignItems: "baseline",
                      justifyContent: "space-between",
                      flex: 1,
                    }}
                  >
                    <span className="payment-page-title-confirm-save-title">
                      Annually
                    </span>
                    <span className="payment-page-title-confirm-save-price">
                      <span className="payment-page-title-confirm-save-inner">
                        ${selectedPlan.id === "business" ? 360 : 80}
                      </span>
                      <span className="payment-page-title-confirm-when-inner">
                        /year
                      </span>
                    </span>
                  </div>
                </div>
                <div style={{ display: "flex", justifyContent: "flex-end" }}>
                  <span
                    className="payment-page-title-confirm-save-tag"
                  >
                    Save ${selectedPlan.id === "business" ? "60" : "16"}/year
                  </span>
                </div>
              </div>

              {/* Monthly option */}
              <div
                style={{
                  border: !isChecked
                    ? "2px solid #325CEB"
                    : "1px solid #D9D9D9",
                  borderRadius: "10px",
                  padding: "12px",
                  cursor: "pointer",
                  background: "#fff",
                }}
                onClick={() => {
                  setIsChecked(false);
                  // 切换计费周期时清除促销码状态
                  if (promoCodeApplied) {
                    setPromoCodeApplied(false);
                    setPromoCode("");
                    setAppliedPromoCode("");
                    setPromoCodeError(false);
                  }
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "baseline",
                    marginBottom: "8px",
                  }}
                >
                  <Radio checked={!isChecked} />
                  <div
                    style={{
                      display: "flex",
                      alignItems: "baseline",
                      justifyContent: "space-between",
                      flex: 1,
                    }}
                  >
                    <span className="payment-page-title-confirm-save-title">
                      Monthly
                    </span>
                    <span className="payment-page-title-confirm-save-price">
                      <span className="payment-page-title-confirm-save-inner">
                        ${selectedPlan.id === "business" ? 35 : 8}
                      </span>
                      <span className="payment-page-title-confirm-when-inner">
                        /month
                      </span>
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* 右侧：Promo code 和 Order Summary */}
            <div style={{ width: isMobile ? "100%" : "567px" }}>
              {/* Promo code */}
              <div
                className="payment-form-content-promo-code"
              >
                <div
                  style={{
                    width: "100%",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    cursor: "pointer",
                    // marginBottom: "8px",
                  }}
                  onClick={() => setPromoCodeExpanded(!promoCodeExpanded)}
                >
                  <div style={{ fontSize: "16px", fontWeight: 500 }}>
                    Promo code
                  </div>
                  <div
                    style={{
                      transform: promoCodeExpanded
                        ? "rotate(180deg)"
                        : "rotate(0deg)",
                      transition: "transform 0.3s ease",
                    }}
                  >
                    {promoCodeExpanded ? "−" : "+"}
                  </div>
                </div>

                {promoCodeExpanded && <div
                  style={{
                    width: "100%",
                    maxHeight: promoCodeExpanded ? "200px" : "0",
                    opacity: promoCodeExpanded ? 1 : 0,
                    overflow: "hidden",
                    transition: "all 0.3s ease-in-out",
                  }}
                >
                  {promoCodeApplied ? (
                    <div
                      style={{
                        borderRadius: "8px",
                        backgroundColor: "#FFFFFF",
                        padding: "12px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                      }}
                    >
                      <div>
                        <div style={{ display: "flex", alignItems: "center" }}>
                          <div
                            style={{
                              width: "16px",
                              height: "16px",
                              borderRadius: "50%",
                              backgroundColor: "#13358D",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              marginRight: "8px",
                              color: "#fff",
                              border: "1px solid #13358D",
                            }}
                          >
                            ✓
                          </div>
                          <div style={{ fontSize: "14px", color: "#586570" }}>
                            Code applied
                          </div>
                        </div>
                        <div style={{ display: "flex", alignItems: "center" }}>
                          <div style={{ marginLeft: "24px", fontSize: "16px" }}>
                            {promoCode.toUpperCase()}
                          </div>
                        </div>
                      </div>
                      <div
                        style={{
                          cursor: "pointer",
                          color: "#C41212",
                          fontSize: "14px",
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRemovePromoCode();
                        }}
                      >
                        Remove
                      </div>
                    </div>
                  ) : (
                    <div style={{ display: "flex", alignItems: "center" }}>
                      <Input
                        placeholder="Enter promo code"
                        value={promoCode}
                        onChange={(e) => {
                          setPromoCode(e.target.value);
                          setPromoCodeError(false);
                        }}
                        style={{
                          flex: 1,
                          height: "44px",
                          backgroundColor: "#FFFFFF",
                          borderColor: promoCodeError ? "#ff4d4f" : "#d9d9d9",
                          borderRadius: "8px",
                        }}
                      />
                      <Button
                        type="secondary"
                        style={{
                          marginLeft: "8px",
                          height: "44px",
                          width: "80px",
                          backgroundColor: "#F7F8FF",
                          borderColor: "#325CEB",
                          borderRadius: "8px",
                          color: "#325CEB",
                          fontWeight: 500,
                        }}
                        onClick={handleApplyPromoCode}
                      >
                        Apply
                      </Button>
                    </div>
                  )}
                  {promoCodeError && (
                    <div
                      style={{
                        color: "#ff4d4f",
                        fontSize: "12px",
                        marginTop: "4px",
                      }}
                    >
                      Invalid promo code!
                    </div>
                  )}
                </div>}
              </div>

              {/* Select your payment method */}
              <div style={{ marginBottom: "16px" }}>
                <PaymentMethodSelector 
                  ref={paymentMethodRef}
                  onFormDataChange={setPaymentFormData}
                  initialData={paymentFormData}
                />
              </div>

              {/* Order Summary */}
              <OrderSummary 
                planType={selectedPlan.id === 'business' ? 'business' : 'individual'}
                billingFrequency={isChecked ? 'annual' : 'monthly'}
                promoCodeApplied={promoCodeApplied}
                promoCodeDiscount={getPromoCodeDiscount()}
                totalPrice={parseFloat(getTotal())}
              />

              {/* 移动端 FAQ 模块 - 显示在 Order Summary 下面 */}
              {isMobile && (
                <div className="question-box mobile-faq">
                  <div className="question-box-title">Frequently asked questions</div>
                  <div className="question-box-content">
                    <div className="question-box-content-item">
                      <div className="question-box-content-item-title">
                        Can I change plans later on?
                      </div>
                      <div className="question-box-content-item-answer">
                        Yes. You can change plans, or cancel anytime by navigating to
                        Plans and Pricing.
                      </div>
                    </div>
                    <div className="question-box-content-item">
                      <div className="question-box-content-item-title">
                        Can I cancel my subscription?
                      </div>
                      <div className="question-box-content-item-answer">
                        You can cancel your subscription anytime by navigating to
                        Billing Management.
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <Button
                type="primary"
                size="large"
                block
                onClick={async () => {
                  // Prevent multiple clicks
                  if (loading) {
                    return;
                  }
                  
                  setLoading(true);
                  try {
                    // 首先验证支付表单
                    console.log("paymentMethodRef:", paymentMethodRef);
                    console.log("paymentMethodRef.current:", paymentMethodRef.current);
                    
                    if (!paymentMethodRef.current) {
                      message.error("Payment form not ready. Please try again.");
                      setLoading(false);
                      return;
                    }
                    
                    const validationResult = await paymentMethodRef.current.validateForm();
                    console.log("Validation result:", validationResult);
                    
                    if (!validationResult?.isValid) {
                      message.error("Please fill in all required payment fields.");
                      setLoading(false);
                      return;
                    }
                    
                    // Ensure stripe customer is loaded before proceeding
                    let customerId = stripeCustomer?.customer?.id;
                    if (!customerId) {
                      message.warning("Loading customer information...");
                      // Fetch customer info directly
                      const token = sessionStorage.getItem("ZeitroA");
                      try {
                        const response = await fetch("/los/getcurrentsubscription", {
                          method: "GET",
                          headers: {
                            Authorization: "Bearer " + token,
                            Cache: "no-cache",
                          },
                        });
                        
                        if (response.ok) {
                          const data = await response.json();
                          const fetchedCustomer = data.stripeCustomer;
                          if (fetchedCustomer?.customer?.id) {
                            customerId = fetchedCustomer.customer.id;
                          }
                        }
                      } catch (error) {
                        console.error("Error fetching customer:", error);
                      }
                      
                      // Check again after fetch
                      if (!customerId) {
                        message.error("Customer information not available. Please refresh the page and try again.");
                        setLoading(false);
                        return;
                      }
                    }
                    
                    // Create setup intent and get the client secret directly, passing the customerId we fetched
                    const clientSecretValue = await createSetupIntent(customerId);
                    
                    if (!clientSecretValue) {
                      message.error("Failed to create payment session. Please try again.");
                      setLoading(false);
                      return;
                    }
                    
                    // Wait a brief moment to ensure state is updated (defensive)
                    await new Promise(resolve => setTimeout(resolve, 100));
                    
                    // Pass the client secret directly to avoid race condition
                    await handlePaymentWithExistingData(clientSecretValue);
                  } catch (error) {
                    console.error("Error processing payment:", error);
                    // Error message is already shown in createSetupIntent or handlePaymentWithExistingData
                    if (!error.message || !error.message.includes("Payment")) {
                      message.error(error.message || "Payment failed. Please try again.");
                    }
                  } finally {
                    setLoading(false);
                  }
                }}
                loading={loading}
                disabled={loading}
                style={{ height: "44px", marginTop: "16px" }}
              >
                Pay now
              </Button>
            </div>
          </div>
          
          {/* PC 端 FAQ 模块 - 保持现状，显示在右侧 */}
          {!isMobile && (
            <div className="question-box">
              <div className="question-box-title">Frequently asked questions</div>
              <div className="question-box-content">
                <div className="question-box-content-item">
                  <div className="question-box-content-item-title">
                    Can I change plans later on?
                  </div>
                  <div className="question-box-content-item-answer">
                    Yes. You can change plans, or cancel anytime by navigating to
                    Plans and Pricing.
                  </div>
                </div>
                <div className="question-box-content-item">
                  <div className="question-box-content-item-title">
                    Can I cancel my subscription?
                  </div>
                  <div className="question-box-content-item-answer">
                    You can cancel your subscription anytime by navigating to
                    Billing Management.
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="payment-form">
      <div className="payment-form-container">
        <div className="payment-form-title">
          Complete Your Payment
        </div>
        <div className="payment-form-subtitle">
          Enter your payment details to complete your subscription.
        </div>
        
        {/* 显示选择的套餐信息 */}
        <div style={{ 
          background: '#f8f9fa', 
          padding: '16px', 
          borderRadius: '8px', 
          marginBottom: '24px',
          border: '1px solid #e9ecef'
        }}>
          <div style={{ fontSize: '14px', color: '#666', marginBottom: '4px' }}>
            Selected Plan
          </div>
          <div style={{ fontSize: '16px', fontWeight: '600', color: '#333' }}>
            {selectedPlan.name} - ${getTotal()} ({isChecked ? 'annual' : 'monthly'} billing)
          </div>
        </div>

        <div style={{ textAlign: 'center', padding: '20px' }}>
          <Spin size="large" />
          <p style={{ marginTop: '10px' }}>Processing payment...</p>
        </div>
      </div>
    </div>
  );
}

// Stripe 支付表单组件 - 只在需要 Stripe hooks 时使用
function StripePaymentForm({ selectedPlan, isChecked, appliedPromoCode, onPaymentSuccess, onBack, loading, setLoading, clientSecret, setClientSecret }) {
  const stripe = useStripe();
  const elements = useElements();

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (selectedPlan.id === "starter") {
      // 免费套餐直接跳转
      onPaymentSuccess();
      return;
    }

    if (!stripe || !elements) {
      message.error("Payment system not ready. Please try again.");
      // 清除支付状态
      setClientSecret("");
      return;
    }

    if (!clientSecret) {
      message.error("Payment session expired. Please try again.");
      // 清除支付状态
      setClientSecret("");
      return;
    }

    // 验证是否选择了套餐
    if (!selectedPlan || !selectedPlan.id) {
      message.error("Please select a plan first.");
      // 清除支付状态
      setClientSecret("");
      return;
    }

    // 验证计费周期选择
    if (selectedPlan.id !== "starter" && typeof isChecked !== 'boolean') {
      message.error("Please select a billing cycle.");
      return;
    }

    setLoading(true);

    try {
      // 参考 planPricing 的实现，使用 CardNumberElement 而不是 PaymentElement
      const cardElement = elements.getElement(CardNumberElement);
      if (!cardElement) {
        message.error('Please enter your card information');
        setLoading(false);
        // 清除支付状态
        setClientSecret("");
        return;
      }

      // 创建支付方式
      const result = await stripe.createPaymentMethod({
        type: "card",
        card: cardElement,
      });

      if (result.error) {
        message.error(result.error.message);
        setLoading(false);
        // 清除支付状态
        setClientSecret("");
        const pay = paymentPayProps(selectedPlan.id, isChecked, 'fail', 'signup')
        if (pay) track(STATSIG.PAYMENT.PAY, pay.value, pay.metadata)
        return;
      }

      // 确认卡片设置
      const { setupIntent, error: setupError } = await stripe.confirmCardSetup(clientSecret, {
        payment_method: result.paymentMethod.id,
      });

      if (setupError) {
        message.error(setupError.message || "Payment setup failed. Please try again.");
        setLoading(false);
        // 清除支付状态
        setClientSecret("");
        const pay = paymentPayProps(selectedPlan.id, isChecked, 'fail', 'signup')
        if (pay) track(STATSIG.PAYMENT.PAY, pay.value, pay.metadata)
        return;
      }

      console.log("setupIntent", {
        paymentMethodId: setupIntent.payment_method,
        billingFrequency: isChecked ? "annual" : "monthly",
        planType: selectedPlan.id,
        selectedLoanOfficerIDs: [], // 注册时使用空数组，表示默认用户
        stripeCustomerID: setupIntent.customer,
        promoCode: appliedPromoCode || null, // 传递促销码
      });

      // return;
      // 创建订阅
      const token = sessionStorage.getItem("ZeitroA");
      const subscriptionResponse = await fetch("/los/subscribetopaidplan", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: "Bearer " + token,
          Cache: "no-cache",
        },
        body: JSON.stringify({
          paymentMethodId: setupIntent.payment_method,
          billingFrequency: isChecked ? "annual" : "monthly",
          planType: selectedPlan.id,
          selectedLoanOfficerIDs: [], // 注册时使用空数组，表示默认用户
          stripeCustomerID: setupIntent.customer,
          promoCode: appliedPromoCode || null, // 传递促销码
        }),
      });

      if (subscriptionResponse.status !== 200) {
        const errorData = await subscriptionResponse.json().catch(() => ({}));
        message.error(errorData.error || 'Something went wrong, please try later');
        setLoading(false);
        // 清除支付状态
        setClientSecret("");
        const pay = paymentPayProps(selectedPlan.id, isChecked, 'fail', 'signup')
        if (pay) track(STATSIG.PAYMENT.PAY, pay.value, pay.metadata)
        return;
      }

      message.success("Subscription created successfully!");
      const pay = paymentPayProps(selectedPlan.id, isChecked, 'success', 'signup')
      if (pay) track(STATSIG.PAYMENT.PAY, pay.value, pay.metadata)
      onPaymentSuccess();
    } catch (error) {
      console.error("Payment error:", error);
      message.error("Payment failed. Please try again.");
      // 清除支付状态
      setClientSecret("");
      const pay = paymentPayProps(selectedPlan.id, isChecked, 'fail', 'signup')
      if (pay) track(STATSIG.PAYMENT.PAY, pay.value, pay.metadata)
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <div className="payment-form-field">
        <label>Card Number</label>
        <CardNumberElement 
          options={{
            style: {
              base: {
                fontSize: '16px',
                color: '#424770',
                '::placeholder': {
                  color: '#aab7c4',
                },
              },
              invalid: {
                color: '#9e2146',
              },
            },
          }}
        />
      </div>
      <div className="payment-form-field">
        <label>Expiration Date</label>
        <CardExpiryElement 
          options={{
            style: {
              base: {
                fontSize: '16px',
                color: '#424770',
                '::placeholder': {
                  color: '#aab7c4',
                },
              },
              invalid: {
                color: '#9e2146',
              },
            },
          }}
        />
      </div>
      <div className="payment-form-field">
        <label>CVC</label>
        <CardCvcElement 
          options={{
            style: {
              base: {
                fontSize: '16px',
                color: '#424770',
                '::placeholder': {
                  color: '#aab7c4',
                },
              },
              invalid: {
                color: '#9e2146',
              },
            },
          }}
        />
      </div>
      <div className="payment-form-actions">
        <Button onClick={onBack}>
          Back
        </Button>
        <Button
          type="primary"
          htmlType="submit"
          loading={loading}
          disabled={!stripe}
          size="large"
        >
          Complete Payment
        </Button>
      </div>
      
      {/* 安全提示 */}
      <div style={{ 
        marginTop: '16px', 
        fontSize: '12px', 
        color: '#666', 
        textAlign: 'center',
        lineHeight: '1.4'
      }}>
        🔒 Your payment information is secured by Stripe. We never store your card details.
      </div>
    </form>
  );
}

// 主组件
export default function PaymentPage() {
  const history = useHistory();
  const location = useLocation();
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [showPayment, setShowPayment] = useState(false);
  const [loading, setLoading] = useState(false);
  const [prices, setPrices] = useState([]);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [displayPlans, setDisplayPlans] = useState(pricingPlans);
  const [selectedPlanId, setSelectedPlanId] = useState("originator");
  const [clientSecret, setClientSecret] = useState("");
  const [stripeCustomer, setStripeCustomer] = useState(null);
  // 新增：保存用户之前选择的计划，用于从支付页面返回时恢复
  const [previousSelectedPlanId, setPreviousSelectedPlanId] = useState(null);

  // 新增：从 URL 参数获取配置
  const urlParams = new URLSearchParams(location.search);
  const pageType = urlParams.get('page'); // 'pay' 或 'plan'
  const selectedPlanParam = urlParams.get('selectedPlan'); // 'individual' 或 'business'
  
  // 新增：移动端检测
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    // 检查用户是否已登录
    const token = sessionStorage.getItem("ZeitroA");
    if (!token) {
      // 如果未登录，重定向到登录页面
      window.location.href = "/";
      return;
    }
    setIsAuthenticated(true);
    fetchPrices();
    fetchUserStripeCustomer();
    
    // 检测移动端
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // 选套餐页 PAGE_VIEW（page=pay 直进结账不算）
  useEffect(() => {
    if (!isAuthenticated) return
    if (pageType === 'pay') return
    track(STATSIG.COMMON.VIEW, PAGE.SIGNUP_PLAN, { page: PAGE.SIGNUP_PLAN })
  }, [isAuthenticated, pageType]);

  // 付款页 PAGE_VIEW（选付费套餐后，或 URL page=pay）
  useEffect(() => {
    if (!isAuthenticated) return
    if (!showPayment || !selectedPlan) return
    track(STATSIG.COMMON.VIEW, PAGE.SIGNUP_PAYMENT, { page: PAGE.SIGNUP_PAYMENT })
  }, [isAuthenticated, showPayment, selectedPlan?.id]);

  // 新增：处理 URL 参数
  useEffect(() => {
    if (selectedPlanParam && !loading) {
      // 根据 selectedPlan 参数设置套餐
      if (selectedPlanParam === 'individual') {
        setSelectedPlanId('originator');
      } else if (selectedPlanParam === 'business') {
        setSelectedPlanId('business');
      }
      
      // 如果 page=pay，直接进入支付流程
      if (pageType === 'pay') {
        const plan = displayPlans.find(p => p.id === (selectedPlanParam === 'individual' ? 'originator' : 'business'));
        if (plan) {
          setSelectedPlan(plan);
          setShowPayment(true);
          // 保存当前选择的计划，用于从支付页面返回时恢复
          setPreviousSelectedPlanId(selectedPlanParam === 'individual' ? 'originator' : 'business');
        }
      }
    } else if (!selectedPlanParam) {
      // 如果URL中没有selectedPlan参数，清除previousSelectedPlanId
      setPreviousSelectedPlanId(null);
    }
  }, [selectedPlanParam, pageType, displayPlans, loading]);

  // 清理函数，组件卸载时清除状态
  useEffect(() => {
    return () => {
      setClientSecret("");
      setShowPayment(false);
      setSelectedPlan(null);
      setLoading(false);
      setPreviousSelectedPlanId(null); // 清理新增的状态
    };
  }, []);



  const fetchUserStripeCustomer = async () => {
    try {
      const token = sessionStorage.getItem("ZeitroA");
      const response = await fetch("/los/getcurrentsubscription", {
        method: "GET",
        headers: {
          Authorization: "Bearer " + token,
          Cache: "no-cache",
        },
      });

      if (response.ok) {
        const data = await response.json();
        const { stripeCustomer } = data;
        if (stripeCustomer) {
          setStripeCustomer(stripeCustomer);
        }
      }
    } catch (error) {
      console.error("Error fetching user stripe customer:", error);
      // 如果获取失败，继续使用 null
    }
  };

  const createSetupIntent = async (customerIdOverride) => {
    try {
      const token = sessionStorage.getItem("ZeitroA");
      
      // Use provided customerId or fall back to stripeCustomer from state
      const customerId = customerIdOverride || stripeCustomer?.customer?.id;
      
      console.log("stripeCustomer in createSetupIntent:", stripeCustomer);
      console.log("customerId being sent:", customerId);

      // Check if customerId is required and available
      if (!customerId) {
        throw new Error("Stripe customer ID is required. Please refresh the page and try again.");
      }

      // 参考 planPricing 的实现
      const response = await fetch("/los/stripe/getorcreatesetupintent", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: "Bearer " + token,
          Cache: "no-cache",
        },
        body: JSON.stringify({
          customerId: customerId
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      if (!data.client_secret) {
        throw new Error("No client secret received from server");
      }

      // Update state and return the client secret
      setClientSecret(data.client_secret);
      return data.client_secret;
    } catch (error) {
      console.error("Error creating setup intent:", error);
      message.error(error.message || "Failed to initialize payment. Please try again.");
      throw error; // 重新抛出错误，让调用者处理
    }
  };

  const fetchPrices = async () => {
    setLoading(true);
    try {
      const response = await fetch("/los/stripe/config");
      if (response.ok) {
        const { prices } = await response.json();
        setPrices(prices);

        // 更新套餐的价格信息
        const updatedPlans = displayPlans.map(plan => {
          const priceInfo = prices.find(p => p.id === plan.priceId);
          if (priceInfo) {
            return {
              ...plan,
              price: priceInfo.unit_amount / 100, // 转换为美元
              currency: priceInfo.currency
            };
          }
          return plan;
        });
        setDisplayPlans(updatedPlans);

        // 更新显示价格 - 修复匹配逻辑
        const updatedPricingPlans = pricingPlans.map((plan) => {
          if (plan.id === "starter") return plan;

          // 根据计划名称匹配价格
          const priceData = prices.find((p) => {
            const displayedName = p.metadata?.displayed_name?.toLowerCase();
            const planName = plan.id.toLowerCase(); // name
            return displayedName && displayedName.includes(planName);
          });

          console.log(
            `Looking for plan ${plan.name}, found price data:`,
            priceData
          );

          if (priceData) {
            return {
              ...plan,
              price: `$${(priceData.unit_amount / 100).toFixed(0)}`,
              priceId: priceData.id,
            };
          }

          // 如果找不到价格数据，保持原样
          console.warn(`Price data not found for plan: ${plan.name}`);
          return plan;
        });
        setDisplayPlans(updatedPlans);
      } else {
        console.error("Failed to fetch prices");
        message.error("Failed to load pricing information. Please try again.");
      }
    } catch (error) {
      console.error("Error fetching prices:", error);
      message.error("Failed to load pricing information. Please try again.");
      // 如果API失败，使用默认价格
      setDisplayPlans(pricingPlans);
    } finally {
      setLoading(false);
    }
  };

  const handlePlanSelect = (planId) => {
    console.log("handlePlanSelect", planId);
    // 只是选择套餐，不执行任何处理逻辑
    setSelectedPlanId(planId);
    
    // 更新previousSelectedPlanId，确保返回时恢复正确的选择
    setPreviousSelectedPlanId(planId);
    
    // 清除之前的支付状态
    setClientSecret("");
    setShowPayment(false);
    setLoading(false);
  };

  const handleActivatePlan = async () => {
    if (!selectedPlanId) {
      message.error("Please select a plan first.");
      return;
    }

    const plan = displayPlans.find((p) => p.id === selectedPlanId);
    console.log("plan", plan, selectedPlanId);

    if (!plan) {
      message.error("Selected plan not found. Please try again.");
      return;
    }

    const planValue = paymentPlanValue(plan.id)
    if (planValue) {
      track(STATSIG.PAYMENT.PLAN, planValue, { plan: planValue })
    }

    if (plan.id === "starter") {
      // 免费套餐直接跳转
      await flushThenLeave(() => {
        handlePaymentSuccess();
      })
      return;
    }

    // 保存当前选择的计划，用于从支付页面返回时恢复
    setPreviousSelectedPlanId(selectedPlanId);
    
    // 设置选中的套餐并跳转到表单填写页面
    setSelectedPlan(plan);
    setShowPayment(true);
  };

  const handlePaymentSuccess = async () => {
    try {
      // 支付成功后，先获取最新的订阅信息
      console.log("Payment successful, fetching updated subscription...");
      
      // 设置加载状态
      setLoading(true);
      
      // 获取最新的订阅信息，添加重试机制
      let retryCount = 0;
      const maxRetries = 1;
      
      while (retryCount < maxRetries) {
        try {
          await fetchUserStripeCustomer();
          break; // 成功获取，跳出重试循环
        } catch (error) {
          retryCount++;
          console.warn(`Failed to fetch subscription (attempt ${retryCount}/${maxRetries}):`, error);
          
          if (retryCount < maxRetries) {
            // 等待一段时间后重试
            await new Promise(resolve => setTimeout(resolve, 1000 * retryCount));
          } else {
            throw error; // 重试次数用完，抛出错误
          }
        }
      }
      
      // 显示成功消息
      message.success("Payment successful! Setting up your subscription...");
      
      // 添加短暂延迟，确保数据同步和用户体验
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // 清除支付相关状态
      setClientSecret("");
      setShowPayment(false);
      setSelectedPlan(null);
      setLoading(false);
      
      // 获取跳转路径
      const utmSource = new URLSearchParams(location.search).get("utm_source");
      const path =
        utmSource === "guidelinegpt"
          ? "/app/management?select=Pricing"
          : "/app/mortgagegpt";
      
      console.log("Redirecting to:", path);
      await flushThenLeave(() => {
        window.location.href = path;
      })
      
    } catch (error) {
      console.error("Error fetching updated subscription after retries:", error);
      
      // 显示警告消息
      // message.warning("Payment successful, but there was an issue updating your subscription. You will be redirected shortly.");
      
      // 即使获取订阅信息失败，也要清除支付状态并跳转
      setClientSecret("");
      setShowPayment(false);
      setSelectedPlan(null);
      setLoading(false);
      
      // 添加短暂延迟后跳转
      setTimeout(async () => {
        const utmSource = new URLSearchParams(location.search).get("utm_source");
        const path =
          utmSource === "guidelinegpt"
            ? "/app/management?select=Pricing"
            : "/app/welcome?signUp=1";
        
        console.log("Redirecting to:", path);
        await flushThenLeave(() => {
          window.location.href = path;
        })
      }, 1500);
    }
  };

  const handleBack = () => {
    console.log("handleBack", showPayment, location.search);
    
    if (showPayment) {
      // 从支付页面返回计划选择页面
      setShowPayment(false);
      setClientSecret(""); // 重置 clientSecret
      setLoading(false); // 重置加载状态
      
      // 恢复之前选择的计划
      if (previousSelectedPlanId) {
        setSelectedPlanId(previousSelectedPlanId);
        // 同时恢复 selectedPlan 对象，保持状态一致性
        const plan = displayPlans.find(p => p.id === previousSelectedPlanId);
        if (plan) {
          setSelectedPlan(plan);
        }
      } else if (selectedPlanParam) {
        // 如果没有之前的选择记录，但有URL参数，则使用URL参数
        if (selectedPlanParam === 'individual') {
          setSelectedPlanId('originator');
          const plan = displayPlans.find(p => p.id === 'originator');
          if (plan) {
            setSelectedPlan(plan);
          }
        } else if (selectedPlanParam === 'business') {
          setSelectedPlanId('business');
          const plan = displayPlans.find(p => p.id === 'business');
          if (plan) {
            setSelectedPlan(plan);
          }
        }
      }
      // 如果没有之前的选择记录且没有URL参数，保持当前选择
    } else {
      console.log("handleBack--", location.search);
      // 如果 URL 中有参数，清除参数并跳转到当前页面
      // if (location.search) {
        history.push('/signupAndPayment');
      // } else {
      //   // 返回注册页面
      //   history.goBack();
      // }
    }
  };

  const svg_check = () => {
    return (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="24"
        height="24"
        viewBox="0 0 24 24"
        fill="none"
      >
        <path
          fill-rule="evenodd"
          clip-rule="evenodd"
          d="M19.6529 6.34713C20.1157 6.80998 20.1157 7.56039 19.6529 8.02324L9.92593 17.7502L4.34713 12.1714C3.88429 11.7085 3.88429 10.9581 4.34713 10.4953C4.80998 10.0324 5.56039 10.0324 6.02324 10.4953L9.92593 14.398L17.9768 6.34713C18.4396 5.88429 19.19 5.88429 19.6529 6.34713Z"
          fill="#2E605A"
        />
      </svg>
    );
  };

  if (!isAuthenticated) {
    return (
      <div className="payment-page-loading">
        <Spin size="large" />
        <p>Checking authentication...</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="payment-page-loading">
        <Skeleton active />
        {/* <p>Loading plans...</p> */}
      </div>
    );
  }

  if (selectedPlan && showPayment) {
    return (
      <div className="payment-page">
        <div className="payment-page-header">
          {/* <img src="/images/logo.svg" alt="Zeitro" className="payment-logo" /> */}
          {/* <h1>Complete Your Registration</h1> */}
        </div>

        <div className="payment-page-content">
          {showPayment ? (
            <Elements 
              stripe={stripePromise}
              options={clientSecret ? {
                clientSecret: clientSecret,
              } : {}}
            >
              <PaymentForm
                selectedPlan={selectedPlan}
                onPaymentSuccess={handlePaymentSuccess}
                onBack={handleBack}
                clientSecret={clientSecret}
                setClientSecret={setClientSecret}
                createSetupIntent={createSetupIntent}
                stripeCustomer={stripeCustomer}
                isMobile={isMobile}
                fetchUserStripeCustomer={fetchUserStripeCustomer}
              />
            </Elements>
          ) : (
            <PaymentForm
              selectedPlan={selectedPlan}
              onPaymentSuccess={handlePaymentSuccess}
              onBack={handleBack}
              clientSecret={clientSecret}
              setClientSecret={setClientSecret}
              createSetupIntent={createSetupIntent}
              stripeCustomer={stripeCustomer}
              isMobile={isMobile}
              fetchUserStripeCustomer={fetchUserStripeCustomer}
            />
          )}
        </div>
      </div>
    );
  }

  const svg_logo = () => {
    return (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="122"
        height="28"
        viewBox="0 0 122 28"
        fill="none"
      >
        <path
          d="M72.6758 6.09116C73.4678 6.09116 74.1349 5.8184 74.6837 5.27289C75.2292 4.72737 75.5019 4.05698 75.5019 3.265C75.5019 2.47301 75.2292 1.80262 74.6837 1.25711C74.1382 0.711591 73.4678 0.435547 72.6758 0.435547C71.8838 0.435547 71.2167 0.708304 70.6679 1.25711C70.1224 1.79934 69.8496 2.47301 69.8496 3.265C69.8496 4.05698 70.1224 4.72409 70.6679 5.27289C71.2134 5.81512 71.8838 6.09116 72.6758 6.09116Z"
          fill="#081265"
        />
        <path
          d="M75.0842 7.47176H70.2666V24.7442H75.0842V7.47176Z"
          fill="#081265"
        />
        <path
          d="M84.8735 10.9403V18.6794C84.8735 19.3432 85.0444 19.8624 85.3894 20.2371C85.7312 20.6117 86.1354 20.799 86.6086 20.799C87.5058 20.799 88.2649 20.3915 88.886 19.5798L90.6835 22.9515C89.185 24.3219 87.6043 25.0054 85.9481 25.0054C84.2918 25.0054 82.8853 24.4632 81.7417 23.3689C80.5981 22.2778 80.0263 20.7892 80.0263 18.9062V10.9436H78.002V7.47662H80.0263V2.30408H84.8735V7.47333H89.047V10.9403H84.8735Z"
          fill="#081265"
        />
        <path
          d="M101.826 11.7069C100.393 11.7069 99.3216 12.2163 98.615 13.2317C97.9085 14.2472 97.5569 15.5912 97.5569 17.2606V24.7401H92.7393V7.47094H97.5569V9.75158C98.1747 9.04504 98.9535 8.44366 99.8835 7.95401C100.814 7.46108 101.76 7.20475 102.723 7.18175L102.756 11.7069H101.826Z"
          fill="#081265"
        />
        <path
          d="M43.5803 7.50485H34.8293L32.1367 11.5438H40.8877L32.1367 24.6702H46.6095L49.3021 20.6313H40.5511L49.3021 7.50485H43.5803Z"
          fill="#081265"
        />
        <path
          fill-rule="evenodd"
          clip-rule="evenodd"
          d="M113.08 25.0047C118.006 25.0047 122 21.0114 122 16.0854C122 11.1594 118.006 7.16618 113.08 7.16618C108.155 7.16618 104.161 11.1594 104.161 16.0854C104.161 21.0114 108.155 25.0047 113.08 25.0047ZM113.08 20.4609C115.497 20.4609 117.456 18.5019 117.456 16.0854C117.456 13.6689 115.497 11.7099 113.08 11.7099C110.664 11.7099 108.705 13.6689 108.705 16.0854C108.705 18.5019 110.664 20.4609 113.08 20.4609Z"
          fill="#081265"
        />
        <path
          fill-rule="evenodd"
          clip-rule="evenodd"
          d="M61.3364 24.5937C63.8699 23.9148 65.8376 22.4349 67.2434 20.153L63.879 18.0335C63.1184 19.4109 61.92 20.3207 60.2882 20.758C59.2792 21.0283 58.2906 20.9836 57.3294 20.622C56.3682 20.2604 55.6927 19.6417 55.3026 18.77L67.4598 15.5125L66.7947 13.0302C66.1465 10.6114 64.8361 8.9212 62.8626 7.96656C60.8885 7.01224 58.7575 6.84158 56.4621 7.45664C54.1673 8.07153 52.3848 9.37926 51.114 11.38C49.8479 13.3829 49.5613 15.6916 50.2656 18.3199C50.9698 20.9481 52.3558 22.8188 54.4309 23.9366C56.5026 25.0519 58.8034 25.2724 61.3364 24.5937ZM54.5469 15.4419C54.1028 13.7844 55.3714 11.8236 57.4363 11.2703C59.5012 10.717 61.5803 11.7809 62.0244 13.4383L54.5469 15.4419Z"
          fill="#081265"
        />
        <path
          fill-rule="evenodd"
          clip-rule="evenodd"
          d="M6.14337 22.9597L5.45206 25.0336C5.12058 26.0281 5.86076 27.055 6.90899 27.055H21.5004C23.1967 27.055 24.5718 25.6799 24.5718 23.9836L24.5722 7.60232C24.5723 5.90596 23.1971 4.53076 21.5007 4.53076H18.4288L14.3335 16.8167H20.5788L18.5312 22.9597H6.14337Z"
          fill="#1630AC"
        />
        <path
          fill-rule="evenodd"
          clip-rule="evenodd"
          d="M6.14298 22.9598H3.07149C1.37515 22.9598 0 21.5847 0 19.8883V3.50704C0 1.8107 1.37515 0.435547 3.07149 0.435547H17.6633C18.7115 0.435547 19.4517 1.46249 19.1203 2.45694L18.4289 4.53087H6.14298L4.09532 10.6738H10.2383L6.14298 22.9598Z"
          fill="#081265"
        />
      </svg>
    );
  };

  return (
    <div className="payment-page">
      <div className="payment-logo"></div>
      <div className="content-box">
        <div className="payment-page-svg">{svg_logo()}</div>
        <div className="payment-page-header">
          <div className="payment-page-header-content">
            <div className="Welcome">Please choose your plan</div>
            <div className="Welcome-sub">
              We will streamline your setup experience accordingly.
            </div>
          </div>
        </div>
        <div className="payment-page-content">
          <Row gutter={[24, 24]} justify="center">
            {displayPlans.map((plan) => (
              <Col xs={24} sm={24} md={8} key={plan.id}>
                <div
                  className={`plan-card ${
                    selectedPlanId === plan.id ? "selected" : ""
                  }`}
                  onClick={() => handlePlanSelect(plan.id)}
                  style={{ cursor: "pointer" }}
                >
                  <div style={{ display: "flex", justifyContent: "flex-end" }}>
                    <Radio checked={selectedPlanId === plan.id}></Radio>
                  </div>
                  <div className="plan-header">
                    <div className="plan-name">{plan.name}</div>
                  </div>
                  <div className="plan-price">
                    <span className="plan-price-text">
                      {plan.price}
                      {plan.name !== "Explorer" && (
                        <span className="plan-unit">/mo</span>
                      )}
                    </span>
                    {plan.name === "Explorer" && (
                      <div style={{ height: 30 }}></div>
                    )}
                    {plan.name === "Individual" && (
                      <span className={`per-user ${selectedPlanId === plan.id ? "tag-bg" : ""}`}>per user</span>
                    )}
                    {plan.name === "Business" && (
                      <span className={`per-company ${selectedPlanId === plan.id ? "tag-bg" : ""}`}>
                        per company (up to 30 users)
                      </span>
                    )}
                    <span className="period">{plan.period}</span>
                    {plan.name === "Business" ? (
                      <>
                        <div className="plan-price-line"></div>
                        <div className="plan-price-line-text">
                          <span className="plan-price-line-text-price">
                            Add additional users
                          </span>
                          <span className="plan-price-line-text-unit">
                            $8
                            <span className="plan-price-line-text-unit-per">
                              /mo
                            </span>
                          </span>
                        </div>
                      </>
                    ) : (
                      <div style={{ height: 54 }}></div>
                    )}
                  </div>

                  <ul className="plan-features">
                    {plan.features.map((feature, index) => (
                      <li key={index}>
                        {/* <CheckOutlined className="feature-icon" /> */}
                        <div
                          className="feature-icon"
                          style={{ marginRight: 8 }}
                        >
                          {svg_check()}
                        </div>
                        <div className="feature-text">{feature}</div>
                      </li>
                    ))}
                  </ul>

                  {/* <Button
                    type={plan.popular ? "primary" : "default"}
                    size="large"
                    block
                    onClick={(e) => {
                      e.stopPropagation();
                      handlePlanSelect(plan);
                    }}
                    className="plan-button"
                  >
                    {plan.buttonText}
                  </Button> */}
                </div>
              </Col>
            ))}
          </Row>

          <div className="payment-page-footer" style={{justifyContent: isMobile ? "center" : "end"}}>
            {/* <Button onClick={handleBack}>Back to Registration</Button> */}
            <div
              // type="primary"
              size="large"
              block
              onClick={(e) => {
                e.stopPropagation();
                if (selectedPlanId) {
                  handleActivatePlan();
                }
              }}
              className="plan-button"
              style={{
                width: selectedPlanId !== "starter" ? "138px" : "163px",
                opacity: selectedPlanId ? 1 : 0.5,
                cursor: selectedPlanId ? "pointer" : "not-allowed",
              }}
            >
              {!selectedPlanId
                ? "Select a plan"
                : selectedPlanId === "starter"
                ? "Get started free"
                : "Activate now"}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// 从官网跳转 注册页面时 这样传参：
// # 选择 free plan  => 注册并进入欢迎页
// https://lo.zeitro.com/signup
// # 选择 individual plan => 注册并直接进入 Individual 套餐支付
// https://lo.zeitro.com/signup?page=pay&selectedPlan=individual
// # 选择 business plan => 注册并直接进入 Business 套餐支付
// https://lo.zeitro.com/signup?page=pay&selectedPlan=business
// # 未选择 plan  => 注册并显示套餐选择页面
// https://lo.zeitro.com/signup?page=plan
